import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'

/** One credential input declared by a provider's adapter class. */
export interface CredentialField {
  name: string
  label: string
  secret: boolean
  required: boolean
}

export type CarrierMode = 'live' | 'dormant' | 'mock'

/**
 * One SPI Freight provider card — registry metadata merged with the
 * carrier_configs row. Credential VALUES are never present; only
 * `credentials_set` (which keys have a stored value).
 */
export interface CarrierCard {
  provider: string
  display_name: string
  capabilities: string[]
  credential_fields: CredentialField[]
  default_api_base: string
  enabled: boolean
  mode: CarrierMode
  api_base: string
  credentials_set: string[]
  extra: Record<string, unknown>
  source: 'db' | 'env'
}

export interface CarriersResponse {
  carriers: CarrierCard[]
}

export interface CarrierHealth {
  provider: string
  display_name: string
  enabled: boolean
  mode: string
  source: string
  capabilities: string[]
  reachable: boolean | null
  last_token_refresh: string | null
  error: string | null
}

export interface CarrierUpdate {
  enabled?: boolean
  mode?: CarrierMode
  api_base?: string
  /** Partial merge: only submitted keys change; empty string clears a key. */
  credentials?: Record<string, string>
}

export function useCarriers() {
  return useQuery({
    queryKey: ['settings', 'carriers'],
    queryFn: () => jsonFetch<CarriersResponse>('/api/admin/carriers'),
    staleTime: 30_000,
  })
}

export function useUpdateCarrier() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ provider, ...body }: CarrierUpdate & { provider: string }) =>
      jsonFetch<CarrierCard>(
        `/api/admin/carriers/${encodeURIComponent(provider)}`,
        { method: 'PUT', body },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'carriers'] })
    },
  })
}

export function useTestCarrier() {
  return useMutation({
    mutationFn: (provider: string) =>
      jsonFetch<CarrierHealth>(
        `/api/admin/carriers/${encodeURIComponent(provider)}/test`,
        { method: 'POST' },
      ),
  })
}
